import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateJobMutation } from '@diditusa/shared/api/jobsApi';
import { useGetProductQuery } from '@diditusa/shared/api/productsApi';
import { CreateJobReq } from '@diditusa/shared/types/jobTypes';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import PickupMap from './PickupMap';
import { Pencil } from 'lucide-react';
import './OrderForm.css';
import { format } from 'date-fns';
import { DateTime } from 'luxon';

const defaultCenter = {
  lat: 36.114647,
  lng: -115.172813,
};

const OrderForm: React.FC = () => {
  const { productId, productTitle } = useParams<{ productId: string; productTitle: string }>();
  const [pickupAddress, setPickupAddress] = useState<string>('');
  const [pickupCoordinates, setPickupCoordinates] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [pickupSuggestions, setPickupSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [pickupDate, setPickupDate] = useState<string>('');
  const [pickupHour, setPickupHour] = useState<string>('12');
  const [pickupMinute, setPickupMinute] = useState<string>('00');
  const [pickupAmPm, setPickupAmPm] = useState<'AM' | 'PM'>('AM');
  const [description, setDescription] = useState<string>('');
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const [step, setStep] = useState<'form' | 'review'>('form');
  const navigate = useNavigate();
  const [createJob, createJobState] = useCreateJobMutation();

  const parsedProductId = productId ? parseInt(productId, 10) : NaN;
  const { data: product, ...getProductState } = useGetProductQuery(parsedProductId, {
    skip: isNaN(parsedProductId),
  });

  const tierName = productTitle || 'Unknown Tier';

  useEffect(() => {
    document.title = `New Order - ${productTitle}`;
  }, [productTitle]);

  useEffect(() => {
    if (createJobState.data) {
      navigate(`/checkout/${createJobState.data.id}`);
    }
  }, [createJobState.data, navigate]);

  useEffect(() => {
    if (pickupDate && pickupHour && pickupMinute && pickupAmPm) {
      let hour = parseInt(pickupHour);
      if (pickupAmPm === 'PM' && hour !== 12) hour += 12;
      if (pickupAmPm === 'AM' && hour === 12) hour = 0;
      const minute = parseInt(pickupMinute);
      const [year, month, day] = pickupDate.split('-').map(Number);
      const selectedDateTime = new Date(year, month - 1, day, hour, minute);

      const now = new Date();
      const minAllowedTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now

      if (selectedDateTime.getTime() <= now.getTime()) {
        setDateTimeError('Please select a future date and time.');
      } else if (selectedDateTime.getTime() <= minAllowedTime.getTime()) {
        setDateTimeError('Please select a time at least 24 hours in the future.');
      } else {
        setDateTimeError(null);
      }
    } else {
      setDateTimeError(null);
    }
  }, [pickupDate, pickupHour, pickupMinute, pickupAmPm]);

  const handlePickupAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPickupAddress(value);

    if (!value.trim()) {
      setIsOutOfRange(false);
      setPickupSuggestions([]);
      return;
    }

    if (value.length > 2 && window.google) {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions({ input: value }, (predictions) => {
        setPickupSuggestions(predictions || []);
      });
    } else {
      setPickupSuggestions([]);
    }
  };

  const handlePickupSuggestionClick = (sugg: google.maps.places.AutocompletePrediction) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: sugg.place_id }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };

        // Calculate distance
        const selectedLatLng = new window.google.maps.LatLng(coords.lat, coords.lng);
        const originLatLng = new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng);
        const distanceMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
          originLatLng,
          selectedLatLng,
        );
        const distanceMiles = distanceMeters / 1609.34;
        const isTooFar = distanceMiles > 50;

        setPickupAddress(results[0].formatted_address);
        setPickupSuggestions([]);
        setPickupCoordinates(coords);
        setIsOutOfRange(isTooFar);
        setAddressError(null);
      }
    });
  };

  const onConfirmClick = () => {
    let hasError = false;

    if (!pickupAddress.trim()) {
      setAddressError('Please enter a pickup address.');
      hasError = true;
    } else {
      setAddressError(null);
    }

    if (isOutOfRange) {
      hasError = true;
    }

    if (hasError) return;

    setStep('review');
  };

  const onBackClick = () => {
    setStep('form');
  };

  const onConfirmAndPayClick = () => {
    if (dateTimeError || !productId || !product) return;
    let hour = parseInt(pickupHour);
    if (pickupAmPm === 'PM' && hour !== 12) hour += 12;
    if (pickupAmPm === 'AM' && hour === 12) hour = 0;
    const minute = parseInt(pickupMinute);
    const [year, month, day] = pickupDate.split('-').map(Number);

    // Create a date for 1:00 PM PST on today’s date
    const pstDate = DateTime.fromObject(
      { year, month: month, day, hour, minute },
      { zone: 'America/Los_Angeles' },
    );

    // Convert to JS Date (if needed)
    const startTime = pstDate.toJSDate();

    const jobReq: CreateJobReq = {
      description: description || 'No additional details provided.',
      durationHours: 5,
      location: pickupAddress,
      startTime,
      securityOptionId: parseInt(productId, 10),
      securityOptionPricingId: product.pricing.id,
    };
    createJob(jobReq);
  };

  const hours = [...Array(12)].map((_, i) => String(i + 1).padStart(2, '0'));
  const minutes = ['00', '30'];
  const format12Hour = (h: string, m: string, ampm: 'AM' | 'PM') => `${h}:${m} ${ampm}`;

  if (!productId || isNaN(parsedProductId)) {
    return <p>Error: Invalid or missing product ID in the URL.</p>;
  }

  if (getProductState.isLoading) {
    return <LoadingIndicator isVisible={true} />;
  }

  if (getProductState.isError) {
    return <p>Error: Could not get product: {(getProductState.error as any).data.message}</p>;
  }

  return (
    <div className='order-form-container'>
      {step === 'form' ? (
        <>
          <h2 className='order-form-title'>Schedule Your Elite Service</h2>
          <h3 className='order-form-title'>Every detail refined. Every moment protected.</h3>
          <div className='order-form-section'>
            <div className='order-map-container'>
              <PickupMap
                address={pickupAddress}
                onAddressSelect={setPickupAddress}
                onCoordinatesSelect={setPickupCoordinates}
                onOutOfRangeChange={setIsOutOfRange}
                origin={defaultCenter}
                radiusInMiles={10}
              />
            </div>
            <label className='order-input-label'>Pick Up Location:</label>
            <div className='order-form-group'>
              <input
                type='text'
                value={pickupAddress}
                onChange={handlePickupAddressChange}
                placeholder='Enter pickup address or place name'
                className='order-input-field'
              />
              {addressError && <p className='order-error-text1'>{addressError}</p>}
              {isOutOfRange && <p className='order-error-text2'>Choose a closer location.</p>}
              {pickupSuggestions.length > 0 && (
                <ul className='order-suggestions-list'>
                  {pickupSuggestions.map((s, i) => (
                    <li
                      key={i}
                      onClick={() => handlePickupSuggestionClick(s)}
                      className='order-suggestion-item'
                    >
                      {s.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className='order-form-row'>
              <label className='order-input-label'>Date:</label>
              <input
                type='date'
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className='order-input-field'
              />

              <label className='order-input-label'>Time:</label>
              <div className='order-time-selectors'>
                <select value={pickupHour} onChange={(e) => setPickupHour(e.target.value)}>
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <select value={pickupMinute} onChange={(e) => setPickupMinute(e.target.value)}>
                  {minutes.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={pickupAmPm}
                  onChange={(e) => setPickupAmPm(e.target.value as 'AM' | 'PM')}
                >
                  <option value='AM'>AM</option>
                  <option value='PM'>PM</option>
                </select>
              </div>
              {dateTimeError && <p className='order-error-text3'>{dateTimeError}</p>}
            </div>

            <div className='order-form-add-instructions'>
              <label className='order-input-label'>Additional Instructions:</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='Provide any details or requests for your experience...'
                className='order-input-field'
                rows={4}
              />
            </div>

            <button
              onClick={onConfirmClick}
              className='order-submit-button'
              disabled={
                !!dateTimeError || !pickupDate || !pickupHour || !pickupMinute || isOutOfRange
              }
            >
              Review
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='order-review-header'>
            <h2 className='order-form-title'>Review Your Order</h2>
            <button className='order-edit-button' onClick={onBackClick} title='Edit'>
              <Pencil size={18} />
            </button>
          </div>
          <div className='order-review-section'>
            <p>
              <strong>Service Option:</strong> {tierName}
            </p>
            <p>
              <strong>Pick Up Location:</strong> {pickupAddress}
            </p>
            <p>
              <strong>Date:</strong> {format(pickupDate, 'MM/dd/yyyy')}
            </p>
            <p>
              <strong>Time:</strong> {format12Hour(pickupHour, pickupMinute, pickupAmPm)}
            </p>
            <p>
              <strong>Duration:</strong> 5 hours
            </p>
            <p>
              <strong>Additional Instructions:</strong> {description || 'None'}
            </p>
          </div>
          <div className='order-button-group'>
            <button onClick={onConfirmAndPayClick} className='submit-button'>
              Confirm & Pay
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderForm;
